import React, { FC } from 'react'
import useFormData from 'hooks/formData'
import InputLabel from 'components/SA/FormElements/InputLabel'
import Recaptcha, { getCaptchaToken } from 'components/SA/FormElements/Recaptcha'
import TextareaLabel from 'components/SA/FormElements/TextareaLabel'
import { newsletterSignupApi } from 'apis/newsletter'
import { REQUEST_SOURCE } from 'server/constants/newsletter_request_sources'
import { formDefaults, formValidationRules } from './helpers'
import { postAALanderContact } from 'apis/artAdvisory'
import classNames from 'classnames'
import styles from '../ArtAdvisory.module.scss'

type ContactUsFormProps = {
  state: Record<string, any>
  handleState: (...args: Array<any>) => any
}

const ContactUsForm: FC<ContactUsFormProps> = (props) => {
  const { state, handleState } = props
  const { formData, formErrors, onChangeInput, updateFormData, validateFormData } = useFormData({
    initialState: formDefaults,
    formRules: formValidationRules,
  })
  const { emailContact, firstName, lastName, budget, message } = formData
  const {
    emailContact: emailContactError,
    firstName: firstNameError,
    budget: budgetError,
  } = formErrors
  const { isBlocking, errorMessage } = state

  // Check for email format error on submit
  const onSubmit = async () => {
    if (isBlocking) {
      return
    }

    window.dataLayer.push({
      event: 'contact_form_start',
    })
    const isValid = await validateFormData()
    handleState((prevState) => ({ ...prevState, isBlocking: true }))

    if (isValid) {
      const captchaAction = 'aa_form_contact_submit'
      const captchaToken = await getCaptchaToken(captchaAction)
      const email = emailContact
      const { success, message: defaultErrorMessage, errorMessages } = await postAALanderContact({
        email,
        firstName,
        lastName,
        budget,
        message,
        captchaAction,
        captchaToken,
      })

      if (success === true) {
        handleState((prevState) => ({ ...prevState, isBlocking: false, hasCompleted: true }))
        const params = {
          email,
          requestSource: REQUEST_SOURCE.AA_QUIZ,
          sourceURL: window.location.href,
        }
        await newsletterSignupApi(params)
        window.dataLayer.push({
          event: 'contact_form_success',
          form_email: email || '',
        })
      } else {
        handleState((prevState) => ({
          ...prevState,
          isBlocking: false,
          hasCompleted: false,
          errorMessage: errorMessages ? errorMessages[0] : defaultErrorMessage,
        }))
      }
    } else {
      handleState((prevState) => ({ ...prevState, isBlocking: false, hasCompleted: false }))
    }
  }

  const onChangeEmailInput = async (ev: React.SyntheticEvent<HTMLInputElement>) => {
    ev.preventDefault()
    const { name, value } = ev.target
    updateFormData({
      [name]: value,
    })
    delete formErrors[name]
  }

  const onFocus = async (ev: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    window.dataLayer.push({
      event: 'contact_form_start',
    })
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault()
      }}
      id='contact'
      className={styles.contactUsForm}
    >
      <h3 className={`${styles.contactUsFormTitle} defaultTypographyH3`}>Contact Us</h3>

      <div className={styles.contactUsFormName}>
        <InputLabel
          error={firstNameError}
          id='firstName'
          maxLength={60}
          name='firstName'
          onChange={onChangeInput}
          onFocus={onFocus}
          title='First Name*'
          type='text'
          value={firstName}
          styleOverride={styles.contactUsFormNameInputField}
        />

        <InputLabel
          id='lastName'
          maxLength={60}
          name='lastName'
          onFocus={onFocus}
          onChange={onChangeInput}
          title='Last Name'
          type='text'
          value={lastName}
          styleOverride={styles.contactUsFormNameInputField}
        />
      </div>

      <InputLabel
        error={emailContactError}
        id='emailContact'
        maxLength={60}
        name='emailContact'
        onFocus={onFocus}
        onChange={onChangeEmailInput}
        title='Email Address*'
        type='text'
        value={emailContact}
        styleOverride={styles.contactUsFormInputField}
      />

      <InputLabel
        error={budgetError}
        id='budget'
        name='budget'
        onFocus={onFocus}
        onChange={onChangeInput}
        title='Budget*'
        type='number'
        value={budget}
        styleOverride={styles.contactUsFormInputField}
      />

      <TextareaLabel
        name='message'
        onChange={onChangeInput}
        onFocus={onFocus}
        placeholder='Help us understand your taste! Provide links to artists, artworks, or your favorite design styles so we can assist you more effectively.'
        title='Notes'
        value={message}
        styleOverride={styles.contactUsFormTextArea}
      />
      <>
        <button
          type='button'
          onClick={onSubmit}
          className={classNames(
            {
              [styles.buttonError]: firstNameError || emailContactError || budgetError,
            },
            'defaultButtonGunMetal',
            styles.submitButton
          )}
          data-action='log-in'
        >
          <p className='defaultTypographyCtaLarge, defaultTextUppercase'>Submit</p>
        </button>
        {errorMessage ? (
          <p className={`${styles.error} defaultTypographyXSmall`}>{errorMessage}</p>
        ) : (
          ''
        )}
      </>
      <Recaptcha styleOverride={styles.contactUsRecaptcha} />

      <span className='defaultTypographyTiny'>
        By submitting this form, you will be subscribed to our Saatchi Art newsletter. You may
        unsubscribe at any time.
      </span>
    </form>
  )
}

export default ContactUsForm
